import React from 'react';
import { PersonaIcon } from './icons';
import { useTheme } from '../context/ThemeContext';

interface PersonalizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPersona: string;
  onPersonaChange: (persona: string) => void;
}

const personas = [
  {
    id: 'default',
    name: 'Default',
    description: 'Standard AI assistant behavior',
    icon: <PersonaIcon persona="default" size={20} />
  },
  {
    id: 'hr',
    name: 'HR',
    description: 'Professional, empathetic HR specialist',
    icon: <PersonaIcon persona="hr" size={20} />
  },
  {
    id: 'sales',
    name: 'Sales',
    description: 'Confident, value-driven sales strategist',
    icon: <PersonaIcon persona="sales" size={20} />
  },
  {
    id: 'operations',
    name: 'Operations',
    description: 'Structured, action-oriented operations manager',
    icon: <PersonaIcon persona="operations" size={20} />
  },
  {
    id: 'it',
    name: 'IT',
    description: 'Precise, solution-focused IT specialist',
    icon: <PersonaIcon persona="it" size={20} />
  },
  {
    id: 'scrum',
    name: 'Scrum Expert',
    description: 'Collaborative, agile-focused Scrum facilitator',
    icon: <PersonaIcon persona="scrum" size={20} />
  },
  {
    id: 'direct',
    name: 'Direct',
    description: 'Straight-to-the-point, no-nonsense answers',
    icon: <PersonaIcon persona="direct" size={20} />
  }
];

const PersonalizationModal: React.FC<PersonalizationModalProps> = ({
  isOpen,
  onClose,
  currentPersona,
  onPersonaChange
}) => {
  const { colors } = useTheme();

  if (!isOpen) return null;

  const handlePersonaSelect = (personaId: string) => {
    onPersonaChange(personaId);
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: colors.surface,
          borderRadius: '12px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: colors.textPrimary
          }}>
            Personalization
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '6px',
              color: colors.textSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = colors.buttonHover}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div style={{
          padding: '24px',
          overflowY: 'auto',
          flex: 1
        }}>
          <p style={{
            margin: '0 0 20px 0',
            fontSize: '14px',
            color: colors.textSecondary,
            lineHeight: '1.5'
          }}>
            Choose a persona to customize how the AI assistant communicates and responds to your needs.
          </p>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {personas.map((persona) => (
              <button
                key={persona.id}
                onClick={() => handlePersonaSelect(persona.id)}
                style={{
                  backgroundColor: currentPersona === persona.id ? colors.buttonHover : 'transparent',
                  border: `2px solid ${currentPersona === persona.id ? colors.primary : colors.border}`,
                  borderRadius: '8px',
                  padding: '16px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}
                onMouseOver={(e) => {
                  if (currentPersona !== persona.id) {
                    e.currentTarget.style.backgroundColor = colors.buttonHover;
                  }
                }}
                onMouseOut={(e) => {
                  if (currentPersona !== persona.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  flexShrink: 0,
                  borderRadius: '10px',
                  background: colors.buttonHover,
                  color: currentPersona === persona.id ? colors.primary : colors.textSecondary,
                }}>
                  {persona.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: colors.textPrimary,
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {persona.name}
                    {currentPersona === persona.id && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: colors.textSecondary,
                    lineHeight: '1.4'
                  }}>
                    {persona.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalizationModal;
